import React, { useState, useRef, useEffect } from 'react';
import ShareStateService from '../../services/ShareStateService';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose }) => {
  const [shareUrl, setShareUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(true);
  const [isCopied, setIsCopied] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Generate URL when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsGenerating(true);
      
      try {
        // Generate the URL
        const url = ShareStateService.createShareableUrl();
        setShareUrl(url);
      } catch (error) {
        console.error('Error generating share URL:', error);
      } finally {
        setIsGenerating(false);
      }
    } else {
      // Reset states when modal closes
      setIsCopied(false);
    }
  }, [isOpen]);

  // Focus on the input when URL is ready
  useEffect(() => {
    if (!isGenerating && isOpen && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isGenerating, isOpen]);

  const handleCopyClick = () => {
    if (inputRef.current) {
      inputRef.current.select();
      navigator.clipboard.writeText(shareUrl)
        .then(() => {
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 3000);
        })
        .catch(err => {
          console.error('Failed to copy URL:', err);
        });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
      <div
        ref={modalRef}
        className="bg-gray-800 rounded-lg max-w-md w-full p-6 shadow-lg border border-gray-700"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-white">Share Your Strategy</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-xl transition-colors"
          >
            ×
          </button>
        </div>

        {isGenerating ? (
          <div className="flex justify-center items-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <p className="ml-3 text-gray-300">Generating link...</p>
          </div>
        ) : (
          <>
            <div className="flex mb-4">
              <input
                ref={inputRef}
                type="text"
                value={shareUrl}
                readOnly
                className="flex-grow p-2.5 bg-gray-700 border border-gray-600 rounded-l text-white text-sm"
              />
              <button
                onClick={handleCopyClick}
                className={`px-4 py-2.5 rounded-r transition-colors text-white font-medium ${
                  isCopied
                    ? 'bg-green-600 hover:bg-green-500'
                    : 'bg-blue-600 hover:bg-blue-500'
                }`}
              >
                {isCopied ? 'Copied!' : 'Copy'}
              </button>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded text-white font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ShareModal;